/**
 * Запись голоса для «Голосом» (макет 14.10.2026).
 *
 * Звук собирается сырым — LPCM 16 кГц, 16 бит, моно — и уходит на сервер в
 * Yandex SpeechKit. Сжатые форматы браузеры пишут разные (Safari — AAC в
 * MP4, Chrome — Opus в WebM), а SpeechKit их не берёт; сырой звук одинаков
 * везде.
 *
 * Распознавание браузера (Web Speech API) не используем: в Chrome голос
 * уходит в Google, в Safari — в Apple, а в нём суммы и названия покупок.
 */
import { unwrap } from './sync.js';

const RATE = 16_000;
/** Дольше 30 секунд не слушаем: фраза о трате короче; на 28-й запись заканчивается сама. */
export const MAX_SECONDS = 28;

export interface Recorder {
  /** Всё записанное с начала — 16 кГц, Int16. */
  pcm: () => ArrayBuffer;
  /** Записанное с прошлого вызова: кусок для потокового распознавания. */
  take: () => ArrayBuffer;
  /** Сколько записано, секунды. */
  seconds: () => number;
  stop: () => void;
}

/** Есть ли в браузере всё, чтобы записать голос. */
export const canRecord = (): boolean => typeof navigator !== 'undefined'
  && !!navigator.mediaDevices?.getUserMedia
  && typeof window !== 'undefined'
  && !!(window.AudioContext ?? (window as unknown as { webkitAudioContext?: unknown }).webkitAudioContext);

/**
 * Начать запись. `onLevel` — громкость от 0 до 1, несколько раз в секунду:
 * по ней качаются полоски, и видно, что тебя слышат.
 */
export async function startRecorder(onLevel: (level: number) => void): Promise<Recorder> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  });
  const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new Ctx();
  // Звук, созданный не прямо в нажатии, Safari может завести на паузе — тогда
  // запись молчит, а полоски стоят.
  if (ctx.state === 'suspended') await ctx.resume().catch(() => {});
  const source = ctx.createMediaStreamSource(stream);
  // ScriptProcessor устарел, но работает везде, включая Safari на iPhone, и
  // не требует отдельного файла, как AudioWorklet.
  const node = ctx.createScriptProcessor(4096, 1, 1);
  const chunks: Int16Array[] = [];
  let total = 0;
  const step = ctx.sampleRate / RATE;
  let carry = 0;
  node.onaudioprocess = (e) => {
    const input = e.inputBuffer.getChannelData(0);
    let sum = 0;
    for (let i = 0; i < input.length; i += 1) sum += input[i]! * input[i]!;
    // Громкость — в децибелах: голос в телефоне тихий, и по прямой шкале
    // полоски сдвигались на пиксель — запись выглядела замершей.
    const db = 20 * Math.log10(Math.sqrt(sum / input.length) + 1e-8);
    onLevel(Math.max(0, Math.min(1, (db + 58) / 40)));
    if (total / RATE >= MAX_SECONDS) return;
    // Понижение частоты средним по окну: для речи этого достаточно, а
    // наложения частот среднее не даёт.
    const out = new Int16Array(Math.floor((input.length - carry) / step));
    let pos = carry;
    for (let i = 0; i < out.length; i += 1) {
      const from = Math.floor(pos);
      const to = Math.min(input.length, Math.floor(pos + step));
      let acc = 0;
      for (let j = from; j < to; j += 1) acc += input[j]!;
      const v = Math.max(-1, Math.min(1, acc / Math.max(1, to - from)));
      out[i] = v < 0 ? v * 0x8000 : v * 0x7fff;
      pos += step;
    }
    carry = pos - input.length;
    chunks.push(out);
    total += out.length;
  };
  source.connect(node);
  node.connect(ctx.destination);
  const join = (from: number): ArrayBuffer => {
    let size = 0;
    for (let i = from; i < chunks.length; i += 1) size += chunks[i]!.length;
    const all = new Int16Array(size);
    let at = 0;
    for (let i = from; i < chunks.length; i += 1) { all.set(chunks[i]!, at); at += chunks[i]!.length; }
    return all.buffer;
  };
  let taken = 0;
  return {
    pcm: () => join(0),
    take: () => { const from = taken; taken = chunks.length; return join(from); },
    seconds: () => total / RATE,
    stop: () => {
      node.onaudioprocess = null;
      try { source.disconnect(); node.disconnect(); } catch { /* уже отключено */ }
      stream.getTracks().forEach((t) => t.stop());
      void ctx.close().catch(() => {});
    },
  };
}

/**
 * Потоковое распознавание: запись открывается на сервере, звук уходит
 * кусками, в ответ на каждый — услышанное к этой секунде.
 *
 * `eou` — SpeechKit услышал конец фразы; `full` — 30 секунд записаны.
 */
export interface Heard { text: string; eou?: boolean; full?: boolean; done?: boolean }

export async function openStream(first: ArrayBuffer): Promise<string> {
  const res = await fetch('/api/ai/stt/start', {
    method: 'POST', body: first, credentials: 'same-origin',
    headers: { 'content-type': 'application/octet-stream' },
  });
  const body = await unwrap(res) as { id?: unknown };
  if (typeof body.id !== 'string') throw new Error('Распознавание не открылось');
  return body.id;
}

/** Очередной кусок; `end` — человек закончил, ответ — весь текст. */
export async function sendStream(id: string, pcm: ArrayBuffer, end = false): Promise<Heard> {
  const res = await fetch(`/api/ai/stt/chunk?id=${encodeURIComponent(id)}${end ? '&end=1' : ''}`, {
    method: 'POST', body: pcm, credentials: 'same-origin',
    headers: { 'content-type': 'application/octet-stream' },
  });
  const body = await unwrap(res) as Record<string, unknown>;
  return {
    text: typeof body.text === 'string' ? body.text : '',
    eou: body.eou === true, full: body.full === true, done: body.done === true,
  };
}

/** Распознать записанное разом — запасной путь, если поток не открылся. */
export async function recognize(pcm: ArrayBuffer): Promise<string> {
  const res = await fetch('/api/ai/stt', {
    method: 'POST', body: pcm, credentials: 'same-origin',
    headers: { 'content-type': 'application/octet-stream' },
  });
  const body = await unwrap(res);
  return typeof (body as { text?: unknown }).text === 'string' ? (body as { text: string }).text : '';
}
